import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ShieldCheck,
  Loader2,
  X as CloseIcon,
  RefreshCw,
  AlertCircle,
  CheckCircle2,
} from "lucide-react";
import { Navbar } from "../components/app/Navbar";
import { Footer } from "../components/app/Footer";
import { AdminAnnouncements } from "../components/app/AdminAnnouncements";
import { AdminHealth } from "../components/app/AdminHealth";
import { DailyActionQueue } from "../components/app/DailyActionQueue";
import { AdminTasks } from "../components/app/AdminTasks";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { toast } from "sonner";

const fmtDate = (v) => {
  if (!v) return "—";
  try {
    return new Date(v).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return "—";
  }
};

const Badge = ({ children, tone = "gray", testid }) => {
  const map = {
    gray: "bg-gray-100 text-gray-700 border-gray-200",
    green: "bg-green-50 text-green-800 border-green-100",
    amber: "bg-amber-50 text-amber-800 border-amber-100",
    red: "bg-red-50 text-red-700 border-red-100",
  };
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border ${map[tone]}`}
      data-testid={testid}
    >
      {children}
    </span>
  );
};

export default function Admin() {
  const navigate = useNavigate();
  const { user, loading: authLoading, refreshMe } = useAuth();

  const [users, setUsers] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState("");
  const [search, setSearch] = useState("");

  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [form, setForm] = useState(null);
  const [savingProfile, setSavingProfile] = useState(false);
  const [actionLoading, setActionLoading] = useState("");
  const [panelMsg, setPanelMsg] = useState({ tone: "", text: "" });

  // ---- Health dashboard state (lifted so DailyActionQueue + AdminHealth
  // share a single /admin/health fetch — saves a redundant round-trip and
  // keeps the action queue and attention list in sync after quick actions). ----
  const [health, setHealth] = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);
  const [healthError, setHealthError] = useState("");

  const loadHealth = useCallback(async () => {
    setHealthLoading(true);
    setHealthError("");
    try {
      const { data } = await api.get("/admin/health");
      setHealth(data);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setHealthError(typeof detail === "string" ? detail : "Couldn't load health metrics.");
    } finally {
      setHealthLoading(false);
    }
  }, []);

  const onAttentionPatched = useCallback((userId, patch) => {
    // Optimistically merge a per-user patch into attention_users so the UI
    // doesn't have to wait for a full refetch. Whitelisted to the fields we
    // can safely refresh client-side (retention + trial dates). Anything not
    // present in `patch` is left untouched.
    const MERGEABLE = [
      "admin_note",
      "admin_status",
      "admin_priority",
      "admin_last_contacted_at",
      "admin_follow_up_at",
      "trial_ends_at",
      "trial_status",
    ];
    setHealth((prev) => {
      if (!prev) return prev;
      const nextAttention = (prev.attention_users || []).map((u) => {
        if (u.user_id !== userId) return u;
        const merged = { ...u };
        for (const k of MERGEABLE) {
          if (Object.prototype.hasOwnProperty.call(patch, k)) {
            merged[k] = patch[k];
          }
        }
        return merged;
      });
      return { ...prev, attention_users: nextAttention };
    });
  }, []);

  // After a DailyActionQueue quick action we just refetch — quick actions
  // change action_queue items (e.g. clearing a follow-up removes the
  // overdue/due-today row entirely), and the rule cascade can promote a
  // new item into the top 15. A full refetch is cheaper than re-running
  // those rules client-side.
  const reloadHealthAfterQueueAction = useCallback(() => {
    loadHealth();
  }, [loadHealth]);

  // Scroll-to-card for "Open user" links from the queue.
  const jumpToAttentionUser = useCallback((userId) => {
    const el = document.querySelector(`[data-testid="admin-attention-row-${userId}"]`);
    if (el) {
      // block: "center" lands the card in the middle of the viewport so the
      // sticky-ish navbar at the top doesn't half-clip it.
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      // Emerald ring + soft glow so the admin's eye lands on the right row.
      // Slightly longer hold (2.5s) gives time to register after scroll.
      el.classList.add("ring-2", "ring-emerald-400", "shadow-lg", "shadow-emerald-200/40");
      setTimeout(
        () => el.classList.remove("ring-2", "ring-emerald-400", "shadow-lg", "shadow-emerald-200/40"),
        2500,
      );
    }
  }, []);

  // ---- Access gate ----
  useEffect(() => {
    // Refresh /auth/me so we have the freshest is_admin value (e.g. user was
    // just granted admin in another tab/session).
    refreshMe();
  }, [refreshMe]);

  const isAdmin = !!user?.is_admin;

  const loadUsers = useCallback(async () => {
    setListLoading(true);
    setListError("");
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data || []);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setListError(typeof detail === "string" ? detail : "Failed to load users.");
    } finally {
      setListLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAdmin) {
      loadUsers();
      loadHealth();
    }
  }, [isAdmin, loadUsers, loadHealth]);

  const openUser = useCallback(async (id) => {
    setSelectedId(id);
    setDetailLoading(true);
    setPanelMsg({ tone: "", text: "" });
    try {
      const { data } = await api.get(`/admin/users/${id}`);
      setDetail(data);
      setForm({
        email: data.email || "",
        business_name: data.business_name || "",
        phone: data.phone || "",
        trial_status: data.trial_status || "expired",
        trial_ends_at: data.trial_ends_at ? data.trial_ends_at.slice(0, 10) : "",
        is_subscribed: !!data.is_subscribed,
        subscription_status: data.subscription_status || "inactive",
        is_admin: !!data.is_admin,
      });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setPanelMsg({
        tone: "error",
        text: typeof detail === "string" ? detail : "Failed to load user.",
      });
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const closeDetail = () => {
    setSelectedId(null);
    setDetail(null);
    setForm(null);
    setPanelMsg({ tone: "", text: "" });
  };

  const applyDetail = (data) => {
    setDetail(data);
    setForm({
      email: data.email || "",
      business_name: data.business_name || "",
      phone: data.phone || "",
      trial_status: data.trial_status || "expired",
      trial_ends_at: data.trial_ends_at ? data.trial_ends_at.slice(0, 10) : "",
      is_subscribed: !!data.is_subscribed,
      subscription_status: data.subscription_status || "inactive",
      is_admin: !!data.is_admin,
    });
    // Reflect the change in the list too.
    setUsers((prev) =>
      prev.map((u) =>
        u.id === data.id
          ? {
              ...u,
              email: data.email,
              business_name: data.business_name,
              phone: data.phone,
              trial_status: data.trial_status,
              trial_ends_at: data.trial_ends_at,
              subscription_status: data.subscription_status,
              is_subscribed: data.is_subscribed,
              is_admin: data.is_admin,
              lead_count: data.lead_count,
            }
          : u
      )
    );
  };

  const saveChanges = async (e) => {
    e.preventDefault();
    if (!form || !selectedId) return;
    setPanelMsg({ tone: "", text: "" });
    setSavingProfile(true);
    try {
      const payload = {
        email: form.email.trim(),
        business_name: form.business_name.trim(),
        phone: form.phone.trim(),
        trial_status: form.trial_status,
        is_subscribed: form.is_subscribed,
        subscription_status: form.subscription_status,
        is_admin: form.is_admin,
      };
      if (form.trial_ends_at) {
        payload.trial_ends_at = new Date(form.trial_ends_at + "T23:59:59Z").toISOString();
      }
      const { data } = await api.patch(`/admin/users/${selectedId}`, payload);
      applyDetail(data);
      setPanelMsg({ tone: "success", text: "Saved." });
      toast.success("User updated");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setPanelMsg({
        tone: "error",
        text: typeof detail === "string" ? detail : "Save failed.",
      });
    } finally {
      setSavingProfile(false);
    }
  };

  const runAction = async (label, fn) => {
    setActionLoading(label);
    setPanelMsg({ tone: "", text: "" });
    try {
      const { data } = await fn();
      applyDetail(data);
      setPanelMsg({ tone: "success", text: `${label} done.` });
      toast.success(label);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setPanelMsg({
        tone: "error",
        text: typeof detail === "string" ? detail : `${label} failed.`,
      });
    } finally {
      setActionLoading("");
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter(
      (u) =>
        (u.email || "").toLowerCase().includes(q) ||
        (u.business_name || "").toLowerCase().includes(q)
    );
  }, [users, search]);

  // ---- Render ----
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="admin-auth-loading">
        <Loader2 className="w-6 h-6 animate-spin text-green-800" />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen flex flex-col bg-[#f9f9f8]" data-testid="admin-denied-page">
        <Navbar />
        <main className="flex-1 flex items-center justify-center p-6">
          <div className="max-w-md w-full bg-white border border-gray-200 rounded-2xl p-10 text-center">
            <div className="w-12 h-12 rounded-full bg-red-100 text-red-700 mx-auto flex items-center justify-center">
              <AlertCircle className="w-6 h-6" />
            </div>
            <h1 className="font-display mt-5 text-2xl font-semibold text-gray-900" data-testid="admin-denied-title">
              Not authorized
            </h1>
            <p className="mt-2 text-gray-600" data-testid="admin-denied-message">
              You don't have access to the admin panel.
            </p>
            <Button
              onClick={() => navigate("/dashboard")}
              className="mt-6 w-full h-11 bg-green-800 hover:bg-green-900 text-white"
              data-testid="admin-denied-back-to-dashboard"
            >
              Back to dashboard
            </Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-[#f9f9f8]" data-testid="admin-page">
      <Navbar />
      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <header className="mb-8 flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-xs tracking-[0.18em] uppercase font-semibold text-green-800">
              <ShieldCheck className="w-3.5 h-3.5" /> Internal
            </div>
            <h1 className="font-display mt-2 text-3xl sm:text-4xl font-semibold text-gray-900 tracking-tight" data-testid="admin-title">
              Admin Panel
            </h1>
            <p className="mt-1 text-gray-500">Manage Tasklio users.</p>
          </div>
          <Button
            variant="outline"
            onClick={loadUsers}
            disabled={listLoading}
            data-testid="admin-refresh-button"
          >
            <RefreshCw className={`w-4 h-4 mr-1.5 ${listLoading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </header>

        <DailyActionQueue
          queue={health?.action_queue || []}
          loading={healthLoading}
          onAfterPatch={reloadHealthAfterQueueAction}
          onJumpToUser={jumpToAttentionUser}
        />

        <div className="mb-4 mt-8">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by email or business name"
            className="h-11 max-w-sm"
            data-testid="admin-search-input"
          />
        </div>

        <div className="rounded-2xl bg-white border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm" data-testid="admin-users-table">
              <thead className="bg-gray-50 text-gray-600 text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold">Business</th>
                  <th className="text-left px-4 py-3 font-semibold">Email</th>
                  <th className="text-left px-4 py-3 font-semibold">Source</th>
                  <th className="text-left px-4 py-3 font-semibold">Trial</th>
                  <th className="text-left px-4 py-3 font-semibold">Subscription</th>
                  <th className="text-right px-4 py-3 font-semibold">Leads</th>
                  <th className="text-left px-4 py-3 font-semibold">Created</th>
                  <th className="text-right px-4 py-3 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {listLoading ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-10 text-center text-gray-500">
                      <Loader2 className="w-5 h-5 animate-spin inline-block" />
                    </td>
                  </tr>
                ) : listError ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-10 text-center text-red-600" data-testid="admin-list-error">
                      {listError}
                    </td>
                  </tr>
                ) : filtered.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-10 text-center text-gray-500">No users match.</td>
                  </tr>
                ) : (
                  filtered.map((u) => (
                    <tr key={u.id} className="hover:bg-gray-50 transition-colors" data-testid={`admin-user-row-${u.id}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900">{u.business_name || "—"}</span>
                          {u.is_admin ? <Badge tone="amber" testid={`admin-badge-${u.id}`}>Admin</Badge> : null}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-700">{u.email}</td>
                      <td className="px-4 py-3">
                        {u.acquisition_source ? (
                          <span
                            className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-violet-50 border border-violet-100 text-violet-700 text-[10px] font-semibold uppercase tracking-wider"
                            data-testid={`admin-user-source-${u.id}`}
                          >
                            {u.acquisition_source}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-300">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <Badge tone={u.trial_status === "active" ? "green" : "gray"}>{u.trial_status || "expired"}</Badge>
                      </td>
                      <td className="px-4 py-3">
                        <Badge tone={u.subscription_status === "active" || u.is_subscribed ? "green" : "gray"}>
                          {u.subscription_status === "active" || u.is_subscribed ? "active" : "inactive"}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-right text-gray-900 font-medium">{u.lead_count ?? 0}</td>
                      <td className="px-4 py-3 text-gray-600">{fmtDate(u.created_at)}</td>
                      <td className="px-4 py-3 text-right">
                        <Button
                          variant="outline"
                          onClick={() => openUser(u.id)}
                          className="h-8 px-3 text-xs"
                          data-testid={`admin-open-user-${u.id}`}
                        >
                          View / Edit
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <p className="mt-6 text-xs text-gray-400">
          Showing {filtered.length} of {users.length} users. Admin actions are logged server-side.
        </p>

        <AdminHealth
          data={health}
          loading={healthLoading}
          error={healthError}
          onRefresh={loadHealth}
          onAttentionPatched={onAttentionPatched}
        />

        <AdminTasks />

        <AdminAnnouncements />

        <p className="mt-2 text-sm text-gray-400">
          <Link to="/dashboard" className="hover:underline" data-testid="admin-back-to-dashboard">← Back to dashboard</Link>
        </p>
      </main>
      <Footer />

      {/* Detail / edit panel */}
      {selectedId ? (
        <div className="fixed inset-0 z-50 flex" data-testid="admin-detail-overlay">
          <div
            className="flex-1 bg-black/40"
            onClick={closeDetail}
            aria-hidden="true"
          />
          <aside className="w-full max-w-lg bg-white shadow-2xl overflow-y-auto" data-testid="admin-detail-panel">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-5 py-4 flex items-center justify-between">
              <h2 className="font-display text-lg font-semibold text-gray-900" data-testid="admin-detail-title">
                Edit user
              </h2>
              <button onClick={closeDetail} className="p-2 rounded-md hover:bg-gray-100" data-testid="admin-detail-close">
                <CloseIcon className="w-5 h-5" />
              </button>
            </div>

            {detailLoading || !form ? (
              <div className="p-10 text-center text-gray-500">
                <Loader2 className="w-5 h-5 animate-spin inline-block" />
              </div>
            ) : (
              <div className="p-5 space-y-5">
                <div className="rounded-lg border border-gray-100 p-4 bg-gray-50 text-sm text-gray-600">
                  <div><span className="text-gray-500">User ID:</span> <span className="font-mono text-xs">{detail?.id}</span></div>
                  <div className="mt-1"><span className="text-gray-500">Stripe customer:</span> <span className="font-mono text-xs">{detail?.stripe_customer_id_masked || "—"}</span></div>
                  <div className="mt-1"><span className="text-gray-500">Trial started:</span> {fmtDate(detail?.trial_started_at)}</div>
                </div>

                <form onSubmit={saveChanges} className="space-y-4">
                  <div>
                    <Label htmlFor="f-email">Email</Label>
                    <Input
                      id="f-email"
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className="mt-1.5 h-10"
                      required
                      data-testid="admin-form-email"
                    />
                  </div>
                  <div>
                    <Label htmlFor="f-bn">Business name</Label>
                    <Input
                      id="f-bn"
                      value={form.business_name}
                      onChange={(e) => setForm({ ...form, business_name: e.target.value })}
                      className="mt-1.5 h-10"
                      required
                      data-testid="admin-form-business-name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="f-ph">SMS phone</Label>
                    <Input
                      id="f-ph"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      placeholder="+1 555 123 4567"
                      className="mt-1.5 h-10"
                      data-testid="admin-form-phone"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="f-ts">Trial status</Label>
                      <select
                        id="f-ts"
                        value={form.trial_status}
                        onChange={(e) => setForm({ ...form, trial_status: e.target.value })}
                        className="mt-1.5 h-10 w-full border border-gray-200 rounded-md px-3 bg-white text-sm"
                        data-testid="admin-form-trial-status"
                      >
                        <option value="active">active</option>
                        <option value="expired">expired</option>
                      </select>
                    </div>
                    <div>
                      <Label htmlFor="f-te">Trial ends at</Label>
                      <Input
                        id="f-te"
                        type="date"
                        value={form.trial_ends_at}
                        onChange={(e) => setForm({ ...form, trial_ends_at: e.target.value })}
                        className="mt-1.5 h-10"
                        data-testid="admin-form-trial-ends-at"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="f-ss">Subscription status</Label>
                      <select
                        id="f-ss"
                        value={form.subscription_status}
                        onChange={(e) => setForm({ ...form, subscription_status: e.target.value })}
                        className="mt-1.5 h-10 w-full border border-gray-200 rounded-md px-3 bg-white text-sm"
                        data-testid="admin-form-subscription-status"
                      >
                        <option value="inactive">inactive</option>
                        <option value="active">active</option>
                      </select>
                    </div>
                    <div className="flex items-end">
                      <label className="flex items-center gap-2 h-10 text-sm cursor-pointer select-none" data-testid="admin-form-is-subscribed-label">
                        <input
                          type="checkbox"
                          checked={form.is_subscribed}
                          onChange={(e) => setForm({ ...form, is_subscribed: e.target.checked })}
                          className="w-4 h-4"
                          data-testid="admin-form-is-subscribed"
                        />
                        <span>is_subscribed</span>
                      </label>
                    </div>
                  </div>
                  <div>
                    <label className="flex items-center gap-2 text-sm cursor-pointer select-none" data-testid="admin-form-is-admin-label">
                      <input
                        type="checkbox"
                        checked={form.is_admin}
                        onChange={(e) => setForm({ ...form, is_admin: e.target.checked })}
                        className="w-4 h-4"
                        data-testid="admin-form-is-admin"
                      />
                      <span>Grant admin access</span>
                    </label>
                  </div>

                  {panelMsg.text ? (
                    <p
                      className={`flex items-start gap-2 text-sm border rounded-md px-3 py-2 ${
                        panelMsg.tone === "error"
                          ? "text-red-700 bg-red-50 border-red-100"
                          : "text-green-800 bg-green-50 border-green-100"
                      }`}
                      data-testid="admin-panel-message"
                    >
                      {panelMsg.tone === "error" ? (
                        <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                      ) : (
                        <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
                      )}
                      <span>{panelMsg.text}</span>
                    </p>
                  ) : null}

                  <div className="flex items-center gap-2 pt-2">
                    <Button
                      type="submit"
                      disabled={savingProfile || !!actionLoading}
                      className="h-10 bg-green-800 hover:bg-green-900 text-white"
                      data-testid="admin-save-changes-button"
                    >
                      {savingProfile ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save changes"}
                    </Button>
                    <Button type="button" variant="outline" onClick={closeDetail} className="h-10">Cancel</Button>
                  </div>
                </form>

                <div className="border-t border-gray-100 pt-5">
                  <h3 className="text-sm font-semibold text-gray-900">Quick actions</h3>
                  <div className="mt-3 grid grid-cols-2 gap-2">
                    <Button
                      variant="outline"
                      onClick={() =>
                        runAction("Expire trial", () => api.post(`/admin/users/${selectedId}/expire-trial`))
                      }
                      disabled={!!actionLoading || savingProfile}
                      data-testid="admin-action-expire-trial"
                    >
                      {actionLoading === "Expire trial" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Expire trial"}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() =>
                        runAction("Extend trial 30 days", () =>
                          api.post(`/admin/users/${selectedId}/extend-trial`, { days: 30 })
                        )
                      }
                      disabled={!!actionLoading || savingProfile}
                      data-testid="admin-action-extend-trial"
                    >
                      {actionLoading === "Extend trial 30 days" ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        "Extend trial +30d"
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() =>
                        runAction("Mark subscribed", () =>
                          api.post(`/admin/users/${selectedId}/mark-subscribed`, {
                            subscription_status: "active",
                          })
                        )
                      }
                      disabled={!!actionLoading || savingProfile}
                      data-testid="admin-action-mark-subscribed"
                    >
                      {actionLoading === "Mark subscribed" ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        "Mark subscribed"
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() =>
                        runAction("Mark unsubscribed", () =>
                          api.post(`/admin/users/${selectedId}/mark-unsubscribed`)
                        )
                      }
                      disabled={!!actionLoading || savingProfile}
                      data-testid="admin-action-mark-unsubscribed"
                    >
                      {actionLoading === "Mark unsubscribed" ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        "Mark unsubscribed"
                      )}
                    </Button>
                  </div>
                </div>

                <div className="border-t border-gray-100 pt-5">
                  <h3 className="text-sm font-semibold text-gray-900">Recent leads</h3>
                  {!detail?.recent_leads?.length ? (
                    <p className="mt-2 text-sm text-gray-500">No leads yet.</p>
                  ) : (
                    <ul className="mt-3 space-y-2" data-testid="admin-recent-leads">
                      {detail.recent_leads.map((ld) => (
                        <li key={ld.id} className="flex items-center justify-between text-sm border border-gray-100 rounded-md px-3 py-2">
                          <div className="min-w-0">
                            <p className="font-medium text-gray-900 truncate">{ld.name || "—"}</p>
                            <p className="text-xs text-gray-500 truncate">{ld.service_type || "—"}</p>
                          </div>
                          <div className="text-right shrink-0">
                            <Badge tone={ld.status === "booked" ? "green" : ld.status === "new" ? "amber" : "gray"}>
                              {ld.status || "—"}
                            </Badge>
                            <p className="mt-1 text-[11px] text-gray-400">{fmtDate(ld.created_at)}</p>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            )}
          </aside>
        </div>
      ) : null}
    </div>
  );
}
